import { useEffect } from 'react';
import { addBreadcrumb } from '@sentry/browser';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { languageTranslationsResource } from '@atlassian/jira-servicedesk-language-settings-translations-resource/src/index.tsx';
import { useRouter, useResource, useRouterActions } from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createHook,
	createStore,
	createStateHook,
	createActionsHook,
} from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	TranslationsState,
	TranslationPanelState,
	TranslationResponse,
} from '../../common/types/index.tsx';
import { actions } from './actions.tsx';
import { getTranslationGroups } from './selectors.tsx';

export const actionsForTest = actions;

type Actions = typeof actions;

const initialState: TranslationsState = {
	currentLanguage: '',
	loading: false,
	error: null,
	selectedPanelId: null,
	translationPanelMap: null, // panelId -> panelData
	cachedOriginalApiFieldsMap: null, // contains the original Api fields data for the user changed fields
	translationTabsWithPanelIds: [], // used to render sidebar in order
	selectedLanguage: null,
	defaultLanguage: null,
	oldApiFieldsMap: null,
};

const store = createStore<TranslationsState, Actions>({
	initialState,
	actions,
});

const getFlaggedTranslationCount = (state: TranslationsState) => {
	const { translationPanelMap } = state;
	if (translationPanelMap === null) {
		return 0;
	}

	const translationPanels = state.translationTabsWithPanelIds?.flatMap(
		(tabs) => tabs.translationPanelIds,
	);

	return translationPanels?.filter((panel) => translationPanelMap[panel].isFlagged).length || 0;
};

export const useLanguageTranslationsResource = () => {
	const {
		data: translationsForLanguage,
		loading,
		error,
		refresh,
	} = useResource<TranslationResponse>(languageTranslationsResource);

	return { data: translationsForLanguage, loading, error, refresh };
};

export const useTranslationController = ({ canInitializeStore = false }) => {
	const [hookState, hookActions] = useStateHook();

	const {
		data: translationsForLanguage,
		loading,
		error,
		refresh,
	} = useLanguageTranslationsResource();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (canInitializeStore && !loading && (error || translationsForLanguage)) {
			const translations = translationsForLanguage || null;
			if (translations) {
				fireOperationalAnalytics(createAnalyticsEvent({}), 'translations fetched');
			}
			const e = error ? new Error(error.message) : null;
			hookActions.initialize(translations, e, loading);
		}
	}, [
		loading,
		error,
		translationsForLanguage,
		hookActions,
		canInitializeStore,
		createAnalyticsEvent,
	]);

	return [hookState, hookActions, error, loading, refresh] as const;
};

export const useFlaggedTranslationCount = createStateHook(store, {
	selector: getFlaggedTranslationCount,
});

export const useFormDirty = createStateHook(store, {
	selector: (state: TranslationsState): boolean => {
		const { selectedPanelId, translationPanelMap } = state;
		if (translationPanelMap === null || selectedPanelId === null) {
			return false;
		}
		addBreadcrumb({
			level: 'debug',
			data: {
				translationPanelMap,
				selectedPanelId,
			},
		});

		if (fg('jsm_fix_missing_translation_panel_id')) {
			// It's possible to have inexistent selectedPanelId from translationPanelMap https://atlassian-2y.sentry.io/issues/6498971693/events/89ec0fdae3ac40d8b4e248b9a7f582c0/ so as a workaround we return false
			return Boolean(translationPanelMap[selectedPanelId]?.isDirty);
		}

		return translationPanelMap[selectedPanelId].isDirty;
	},
});

export const useTranslationGroups = createStateHook(store, {
	selector: (state) => ({
		currentLanguage: state.currentLanguage,
		translationGroups: getTranslationGroups(state),
	}),
});

export const useTranslationStoreState = createStateHook(store, {
	selector: (state) => state,
});
export const useFieldState = (translationFieldId: string) => {
	const translationPanel = useSelectedPanel();
	if (!translationPanel) {
		return null;
	}
	const fieldState = translationPanel.translationGroups
		.flatMap((group) => group.translationFields)
		.find((field) => field.logicalId === translationFieldId);

	if (!fieldState) {
		return null;
	}
	return fieldState;
};

export const useSelectedPanel = (): TranslationPanelState | null => {
	const [hookState, hookActions] = useStateHook();

	const [routerState] = useRouter();
	const { sidebarItemKey, languageKey, projectKey } = routerState?.match?.params || {};

	const { replace } = useRouterActions();

	if (hookState.loading) {
		return null;
	}

	if (!hookState.selectedLanguage) {
		return null;
	}

	const { translationPanelMap } = hookState;
	if (!hookState.translationTabsWithPanelIds) {
		return null;
	}

	const allTranslationPanelIds = hookState.translationTabsWithPanelIds.flatMap(
		(translationTab) => translationTab.translationPanelIds,
	);

	if (!translationPanelMap) {
		return null;
	}

	if (!sidebarItemKey) {
		const flaggedPanelId = allTranslationPanelIds.find(
			(translationPanelId) => translationPanelMap[translationPanelId].isFlagged,
		);

		const selectPanelId = flaggedPanelId || allTranslationPanelIds[0];

		const baseSettingsUrl = `/jira/servicedesk/projects/${projectKey}/settings/language/${languageKey}/${selectPanelId}`;
		replace(baseSettingsUrl);
		return null;
	}
	if (hookState.selectedPanelId && hookState.selectedPanelId === sidebarItemKey) {
		return translationPanelMap[hookState.selectedPanelId];
	}

	const selectedPanelId = translationPanelMap[sidebarItemKey]
		? sidebarItemKey
		: allTranslationPanelIds[0];
	const selectedPanel = translationPanelMap[selectedPanelId];

	hookActions.setSelectedPanelId(selectedPanelId);
	return selectedPanel;
};

export const useManageTranslationsActions = createActionsHook(store);
export const useStateHook = createHook<TranslationsState, Actions>(store);
